/**
 * Nakama Service
 * Main service for managing Nakama socket connections and global listeners
 * Delegates to specialized modules for client, match, and message routing
 */

import type { DefaultSocket, Session, Socket, Match, MatchData, MatchPresenceEvent, Presence } from '@heroiclabs/nakama-js';
import { getCurrentConfig } from '../../config/nakama.config';
import { nakamaClientManager } from './nakama-client';
import { nakamaMatchManager } from './nakama-match';
import { decodeMatchData, decompressGzip, COMPRESSION_FLAG_BIT } from './data-utils';
import { handlePresenceLeave } from '../../domains/players';
import { handleDropped, handleReconnectBarred, handleReconnectOver, reconnectAllowed } from '../../domains/connection-lost';
import { reconnectService } from './reconnect-service';

/**
 * Type for message router handler function
 * Implementations should route messages to appropriate domain handlers based on opCode
 */
export type MessageRouterHandler = (opCode: number, data: unknown) => void;

/**
 * NakamaService provides a centralized way to interact with the Nakama server
 * This is a singleton pattern to ensure only one service instance exists
 */
class NakamaService {
  private socket: Socket | null = null;
  private messageRouter: MessageRouterHandler | null = null;
  /**
   * Match data still on its way to the router, in the order it arrived: a
   * compressed message is decompressed before it is routed, and nothing
   * behind it is routed before it. The join's snapshot is mostly compressed
   * and ends with a small message, which would otherwise be routed first.
   */
  private inbound: Promise<void> = Promise.resolve();
  private inboundWaiting = 0;
  /** Counts the player's leaves: a message still decompressing as they left is not routed after it. */
  private leaves = 0;
  /** A simulated drop's pause before it is handled as one: the player leaving cancels it. */
  private simulatedDrop: ReturnType<typeof setTimeout> | null = null;

  /**
   * Create and get the WebSocket socket instance
   * The socket must be created after authentication
   */
  createSocket(): Socket {
    const client = nakamaClientManager.getClient();
    const config = getCurrentConfig();

    this.socket = client.createSocket(config.useSSL, false);

    // Share socket with match manager
    nakamaMatchManager.setSocket(this.socket);

    console.log('[NakamaService] Socket created');

    return this.socket;
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Register a message router handler
   * Called by domains/domain-message-router.ts during app initialization
   * This allows the router to be injected without creating a direct dependency
   */
  registerMessageRouter(handler: MessageRouterHandler): void {
    this.messageRouter = handler;
    console.log('[NakamaService] Message router registered');
  }

  /**
   * Connect the WebSocket socket
   * Automatically sets up global listeners that hand what arrives to the domains. The
   * listeners go on the socket this call connected, and only while it is
   * still the service's: a socket the player left while it connected gets
   * none.
   */
  async connect(session: Session): Promise<void> {
    if (!this.socket) {
      this.createSocket();
    }

    const socket = this.socket;
    if (!socket) {
      throw new Error('Failed to create socket');
    }

    try {
      console.log('[NakamaService] Connecting socket...');
      await socket.connect(session, true);
      console.log('[NakamaService] Socket connected successfully');

      // Setup global listeners immediately after connection
      if (socket === this.socket) this.setupGlobalListeners(socket);
    } catch (error) {
      console.error('[NakamaService] Socket connection failed:', error);
      throw error;
    }
  }

  /**
   * Setup global listeners that hand what arrives to the domains — the
   * router's handlers, the players domain's for a presence leaving — which
   * update the stores. This ensures messages are captured even before
   * components mount.
   */
  private setupGlobalListeners(socket: Socket): void {
    console.log('[NakamaService] Setting up GLOBAL listeners (the domains update the stores)');

    // Match presence events (players joining/leaving)
    socket.onmatchpresence = (event: MatchPresenceEvent) => {
      console.log('[NakamaService] Global listener - Match presence event:', {
        joins: event.joins?.length || 0,
        leaves: event.leaves?.length || 0,
      });

      // Handle players leaving — mark as disconnected rather than removing.
      // The server sends player_disconnected (grace period) followed by player_left
      // (grace expired) as custom match messages; the presence event fires first so we
      // use it to show the dimmed state immediately — through the players domain's
      // handler, which knows the leave of a session the player has left behind (a
      // reloaded page's) for the stale news it is.
      if (event.leaves) {
        event.leaves.forEach((presence: Presence) => {
          console.log('[NakamaService] Player disconnected (presence leave):', presence.user_id);
          handlePresenceLeave({ user_id: presence.user_id, session_id: presence.session_id });
        });
      }

      // Joins are handled via match data messages with full player info
    };

    // Match data events (game state updates)
    // This is the core routing point - all game messages come through here,
    // in the order they arrive (receiveMatchData).
    socket.onmatchdata = (data: MatchData) => this.receiveMatchData(data);

    // Disconnect events - only triggered for UNEXPECTED disconnections
    // (manual disconnects remove this handler before disconnecting)
    socket.ondisconnect = () => {
      this.handleUnexpectedDisconnect();
    };

    // Error events
    socket.onerror = (error: unknown) => {
      console.error('[NakamaService] Socket error:', error);
    };

    console.log('[NakamaService] Global listeners configured - the domains update the stores');
  }

  /**
   * Takes a message off the socket for the router, in arrival order. Large
   * messages are gzip-compressed by the server and flagged via
   * COMPRESSION_FLAG_BIT on op_code, and decompressing one takes a moment:
   * a message that arrives meanwhile waits its turn. One with nothing ahead
   * of it and nothing to decompress is routed at once, as it arrives.
   */
  private receiveMatchData(data: MatchData): Promise<void> | void {
    const compressed = (data.op_code & COMPRESSION_FLAG_BIT) !== 0;
    if (!compressed && this.inboundWaiting === 0) {
      this.routeMatchData(data.op_code, data.data);
      return;
    }

    const leaves = this.leaves;
    this.inboundWaiting += 1;
    this.inbound = this.inbound.then(async () => {
      try {
        // Nakama's typings declare `data.data` as Uint8Array, but some socket
        // adapters deliver a raw ArrayBuffer — normalize before decompressing.
        const raw: Uint8Array | ArrayBuffer = compressed
          ? await decompressGzip(data.data instanceof ArrayBuffer ? new Uint8Array(data.data) : data.data)
          : data.data;
        // Routed even though its socket has dropped since — a ban's notice
        // is followed by the close — but not once the player has left.
        if (leaves === this.leaves) this.routeMatchData(data.op_code & ~COMPRESSION_FLAG_BIT, raw);
      } catch (error) {
        console.error('[NakamaService] ❌ Failed to decompress match data:', error);
      } finally {
        this.inboundWaiting -= 1;
      }
    });
    return this.inbound;
  }

  /**
   * Decodes a message and hands it to the router. The player's own join,
   * which ends a join's snapshot, is noticed by its handler
   * (domains/players.ts, expectOwnJoin), as it is routed.
   */
  private routeMatchData(opCode: number, raw: Uint8Array | ArrayBuffer): void {
    try {
      const decoded = decodeMatchData(raw);

      // Route to appropriate domain handler via registered router
      if (this.messageRouter) {
        this.messageRouter(opCode, decoded);
      } else {
        console.warn('[NakamaService] ⚠️ No message router registered! Message not handled:', opCode);
      }
    } catch (error) {
      console.error('[NakamaService] ❌ Failed to decode/parse match data:', error);
      console.error('[NakamaService] Raw data:', raw);
    }
  }

  /**
   * Shared handler for unexpected disconnections (network drop or simulated).
   * The domain hears of the drop first (handleDropped: what it ends, and the
   * "Reconnecting..." banner); then the soft-reconnect flow attempts to
   * silently rejoin before falling back to the "Connection Lost" modal.
   * After a ban there is no such flow: the close is the server's, and the
   * ban notice stays the one thing on screen (connection-lost.ts). A socket
   * a reconnect already rejoined on, dropping before its join's snapshot,
   * fails that attempt: the reconnect under way tries again.
   */
  private handleUnexpectedDisconnect(): void {
    console.warn('[NakamaService] ⚠️ UNEXPECTED socket disconnection detected');

    handleDropped();

    if (!reconnectAllowed()) {
      console.warn('[NakamaService] Not reconnecting: the session was ended by a ban');
      return;
    }

    if (reconnectService.inProgress()) {
      reconnectService.noteDropped();
      return;
    }

    // The banner, up since the drop, stays until the game is back — its
    // join's snapshot landed — or every attempt has failed. A reconnect
    // stopped by the player leaving, or by a ban, says nothing more.
    void reconnectService.attemptReconnect().then((outcome) => {
      if (outcome === 'stopped') return;
      handleReconnectOver(outcome === 'failed');
    });
  }

  /**
   * Closes a socket for good, saying nothing: its handlers go first, so its
   * close does not read as a drop, and it stops being the service's socket if
   * it was. A failed reconnect attempt's socket goes this way, and so does the
   * socket a player leaves.
   */
  closeSocket(socket: Socket): void {
    socket.ondisconnect = () => {};
    socket.onerror = () => {};
    socket.onmatchpresence = () => {};
    socket.onmatchdata = () => {};
    socket.disconnect(true);
    if (this.socket === socket) {
      this.socket = null;
      nakamaMatchManager.setSocket(null);
    }
  }

  /**
   * Disconnect the WebSocket socket: the player leaves. A reconnect under
   * way stops first and for good — otherwise it would go on and rejoin in
   * the background — as does one a simulated drop has yet to start, and the
   * socket closes without reading as a drop.
   */
  disconnect(): void {
    this.leaves += 1;
    if (this.simulatedDrop !== null) {
      clearTimeout(this.simulatedDrop);
      this.simulatedDrop = null;
    }
    reconnectService.cancel();
    if (this.socket) {
      console.log('[NakamaService] Manual disconnect - closing the socket');
      this.closeSocket(this.socket);
      console.log('[NakamaService] Socket disconnected successfully');
    }
  }

  /**
   * Ends the session's reconnecting for good, at once: a ban
   * (domains/ban-notification.ts), or whatever else ends a session with the
   * socket left as it is. A drop from now on starts no reconnect
   * (connection-lost.ts, reconnectBarred), and one under way stops, the
   * attempt on its way let go. The one call for it: barring alone would leave
   * a reconnect under way running. Disconnect stops one too, for the player
   * leaving.
   */
  stopReconnecting(): void {
    handleReconnectBarred();
    reconnectService.cancel();
  }

  /**
   * Simulate an unexpected disconnection for testing the soft reconnect flow.
   * Closes the socket silently (suppressing ondisconnect to avoid double-fire),
   * waits 5 seconds (simulating a real network drop), then triggers the reconnect flow.
   * A Disconnect in those 5 seconds cancels it, as it stops a real reconnect:
   * otherwise the reconnect would start behind the sign-in screen, with no
   * session to rejoin, and end in Connection lost there.
   */
  simulateDisconnect(): void {
    if (!this.socket) {
      console.warn('[NakamaService] simulateDisconnect: no active socket');
      return;
    }
    console.warn('[NakamaService] 🧪 Simulating unexpected disconnect — will attempt reconnect in 5s');

    this.closeSocket(this.socket);

    // The drop shows at once, the reconnecting banner up, so the player sees the disconnected state.
    handleDropped();

    // Wait 5 seconds before attempting to reconnect, mimicking a real network drop.
    this.simulatedDrop = setTimeout(() => {
      this.simulatedDrop = null;
      this.handleUnexpectedDisconnect();
    }, 5000);
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.socket !== null && nakamaClientManager.isAuthenticated();
  }

  /**
   * True while the game reconnects after a drop: its socket is closed, or new
   * and not back in the match until the join's snapshot has landed — the
   * server ignores a command sent before the join completes, and until the
   * snapshot lands the screen shows the game as it stood before the drop, so
   * a command pressed again could be carried out twice. The command gate
   * sends nothing meanwhile.
   */
  isRejoining(): boolean {
    return reconnectService.inProgress();
  }

  /**
   * The round trip to the server now, in whole milliseconds: one ping over the
   * socket. Null with no socket, or when the ping fails.
   */
  async measureRoundTrip(): Promise<number | null> {
    if (!this.socket) return null;
    try {
      const started = performance.now();
      // send() is on the concrete DefaultSocket, not the Socket interface.
      await (this.socket as DefaultSocket).send({ ping: {} });
      return Math.round(performance.now() - started);
    } catch {
      return null;
    }
  }

  /**
   * Reset the service (useful for logout)
   */
  reset(): void {
    this.disconnect();
    nakamaClientManager.reset();
    nakamaMatchManager.reset();
    console.log('[NakamaService] Service reset complete');
  }

  // ==================== DELEGATE METHODS ====================
  // These methods delegate to the appropriate specialized modules

  /**
   * Authenticate with custom ID
   * Delegates to nakamaClientManager
   */
  async authenticateCustom(
    nakamaCustomId: string,
    gameName: string,
    worldName: string,
    role?: string,
    password?: string
  ): Promise<Session> {
    return nakamaClientManager.authenticateCustom(nakamaCustomId, gameName, worldName, role, password);
  }

  /**
   * Update player account after authentication
   * Delegates to nakamaClientManager
   */
  async updatePlayerAccountAfterAuthenticate(
    gameName: string,
    worldName: string,
    role?: string,
    cohortId?: string,
    displayName?: string
  ): Promise<void> {
    return nakamaClientManager.updatePlayerAccountAfterAuthenticate(gameName, worldName, role, cohortId, displayName);
  }

  /**
   * Create game match via RPC
   * Delegates to nakamaMatchManager
   */
  async createGameMatch(
    gameName: string,
    worldName: string,
    opts?: { sessionId?: string },
  ): Promise<{ match_id: string; game_name: string; world_name: string }> {
    return nakamaMatchManager.createGameMatch(gameName, worldName, opts);
  }

  /**
   * Join a match via socket
   * Delegates to nakamaMatchManager
   */
  async joinMatch(matchId: string, metadata?: Record<string, string>): Promise<Match> {
    return nakamaMatchManager.joinMatch(matchId, metadata);
  }

  /**
   * Get client instance
   * Delegates to nakamaClientManager
   */
  getClient() {
    return nakamaClientManager.getClient();
  }

  /**
   * Get current session
   * Delegates to nakamaClientManager
   */
  getSession() {
    return nakamaClientManager.getSession();
  }
}

// Export singleton instance
export const nakamaService = new NakamaService();
